import React, { useState, useEffect } from 'react';
import {
  Stack,
  Title,
  Button,
  Group,
  Table,
  Badge,
  ActionIcon,
  Paper,
  Text,
  Modal,
  TextInput,
  Select,
  Pagination,
  Loader,
  Center,
  Grid,
  Card
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconPlus,
  IconEdit,
  IconEye,
  IconTrash,
  IconSearch,
  IconRefresh,
  IconUsers,
  IconCreditCard,
  IconUserPlus,
  IconSchool,
  IconLink
} from '@tabler/icons-react';
import { memberService } from '../../services/memberService';
import { batchService } from '../../services/batchService';
import MemberForm from '../../components/admin/MemberForm';
import MemberDetails from '../../components/admin/MemberDetails';

const MemberManager = () => {
  const [members, setMembers] = useState([]);
  const [batches, setBatches] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState(null);
  const [formMode, setFormMode] = useState('create');

  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchValue, 300);
  const [selectedBatch, setSelectedBatch] = useState('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [formOpened, formHandlers] = useDisclosure(false);
  const [detailsOpened, detailsHandlers] = useDisclosure(false);
  const [deleteOpened, deleteHandlers] = useDisclosure(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadMembers();
  }, [debouncedSearch, selectedBatch, selectedPaymentMethod, currentPage]);

  const loadInitialData = async () => {
    try {
      const [batchesData, statsData] = await Promise.all([
        batchService.getBatches(),
        memberService.getStats()
      ]);
      setBatches(batchesData);
      setStats(statsData);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to load initial data',
        color: 'red',
      });
    }
  };

  const loadMembers = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        per_page: 20,
        ...(debouncedSearch && { search: debouncedSearch }),
        ...(selectedBatch && { batch_id: selectedBatch }),
        ...(selectedPaymentMethod && { payment_method: selectedPaymentMethod }),
      };

      const data = await memberService.getMembers(params);
      setMembers(data.data);
      setTotalPages(data.last_page);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to load members',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedMember(null);
    setFormMode('create');
    formHandlers.open();
  };

  const handleEdit = (member) => {
    setSelectedMember(member);
    setFormMode('edit');
    formHandlers.open();
  };

  const handleView = (member) => {
    setSelectedMember(member);
    detailsHandlers.open();
  };

  const handleDelete = (member) => {
    setSelectedMember(member);
    deleteHandlers.open();
  };

  const confirmDelete = async () => {
    try {
      await memberService.deleteMember(selectedMember.id);
      notifications.show({
        title: 'Success',
        message: 'Member deleted successfully',
        color: 'green',
      });
      deleteHandlers.close();
      setSelectedMember(null);
      loadMembers();
      loadInitialData(); // Refresh stats
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to delete member',
        color: 'red',
      });
    }
  };

  const handleFormSubmit = () => {
    formHandlers.close();
    setSelectedMember(null);
    loadMembers();
    loadInitialData(); // Refresh stats
  };

  const clearFilters = () => {
    setSearchValue('');
    setSelectedBatch('');
    setSelectedPaymentMethod('');
    setCurrentPage(1);
  };

  const getPaymentBadge = (paymentMethod) => {
    return paymentMethod === 'Razorpay'
      ? <Badge color="green">Razorpay</Badge>
      : <Badge color="blue">Manual</Badge>;
  };

  const getBatchBadge = (batch) => {
    if (!batch) return <Badge color="gray">No Batch</Badge>;
    return <Badge color="purple">Batch #{batch.batch_number}</Badge>;
  };

  const StatCard = ({ icon: Icon, title, value, color = 'blue' }) => (
    <Card withBorder p="md">
      <Group>
        <Icon size={24} color={`var(--mantine-color-${color}-6)`} />
        <Stack gap="xs">
          <Text size="sm" c="dimmed" fw={500}>
            {title}
          </Text>
          <Text size="lg" fw={700}>
            {value}
          </Text>
        </Stack>
      </Group>
    </Card>
  );


  const handleGenerateAutoLogin = async (member) => {
    try {
      const result = await memberService.generateAutoLoginLink(member.id);

      // Copy to clipboard
      await navigator.clipboard.writeText(result.auto_login_url);

      notifications.show({
        title: 'Auto-Login Link Generated',
        message: `Link copied to clipboard! Valid until ${new Date(result.expires_at).toLocaleString('en-IN')}`,
        color: 'green',
      });
    } catch (error) {
      if (error.name === 'NotAllowedError') {
        // Fallback for clipboard access
        notifications.show({
          title: 'Auto-Login Link Generated',
          message: 'Please copy the link from the browser address bar',
          color: 'blue',
        });

        // Show the link in a prompt as fallback
        const result = await memberService.generateAutoLoginLink(member.id);
        prompt('Copy this auto-login link:', result.auto_login_url);
      } else {
        notifications.show({
          title: 'Error',
          message: 'Failed to generate auto-login link',
          color: 'red',
        });
      }
    }
  };


  return (
    <Stack>
      <Group justify="space-between">
        <Title order={1}>Member Manager</Title>
        <Group>
          <Button
            leftSection={<IconRefresh size={16} />}
            variant="light"
            onClick={() => {
              loadMembers();
              loadInitialData();
            }}
          >
            Refresh
          </Button>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={handleCreate}
          >
            Add Member
          </Button>
        </Group>
      </Group>

      {/* Stats Cards */}
      <Grid>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <StatCard
            icon={IconUsers}
            title="Total Members"
            value={stats.total_members || 0}
            color="blue"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <StatCard
            icon={IconCreditCard}
            title="Razorpay Payments"
            value={stats.razorpay_members || 0}
            color="green"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <StatCard
            icon={IconUserPlus}
            title="Manual Entries"
            value={stats.manual_members || 0}
            color="orange"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <StatCard
            icon={IconSchool}
            title="Assigned to Batch"
            value={stats.members_with_batch || 0}
            color="purple"
          />
        </Grid.Col>
      </Grid>

      {/* Filters */}
      <Paper withBorder p="md">
        <Group>
          <TextInput
            placeholder="Search by name or phone..."
            leftSection={<IconSearch size={16} />}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            style={{ flex: 1 }}
          />
          <Select
            placeholder="Filter by batch"
            data={[
              { value: '', label: 'All Batches' },
              ...batches.map(batch => ({
                value: batch.id.toString(),
                label: `Batch #${batch.batch_number}`
              }))
            ]}
            value={selectedBatch}
            onChange={setSelectedBatch}
            clearable
          />
          <Select
            placeholder="Payment method"
            data={[
              { value: '', label: 'All Methods' },
              { value: 'Razorpay', label: 'Razorpay' },
              { value: 'manual', label: 'Manual' }
            ]}
            value={selectedPaymentMethod}
            onChange={setSelectedPaymentMethod}
            clearable
          />
          <Button variant="light" onClick={clearFilters}>
            Clear
          </Button>
        </Group>
      </Paper>

      {/* Members Table */}
      {loading ? (
        <Center h={400}>
          <Loader size="lg" />
        </Center>
      ) : (
        <Paper withBorder>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Phone</Table.Th>
                <Table.Th>Payment</Table.Th>
                <Table.Th>Batch</Table.Th>
                <Table.Th>Created</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {members.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <Center p="xl">
                      <Stack align="center">
                        <IconUsers size={48} color="gray" />
                        <Text c="dimmed">No members found</Text>
                      </Stack>
                    </Center>
                  </Table.Td>
                </Table.Tr>
              ) : (
                members.map((member) => (
                  <Table.Tr key={member.id}>
                    <Table.Td>
                      <Text fw={500}>{member.full_name}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">
                        {member.phone_number_country_code} {member.phone_number}
                      </Text>
                    </Table.Td>
                    <Table.Td>{getPaymentBadge(member.payment_method)}</Table.Td>
                    <Table.Td>{getBatchBadge(member.batch)}</Table.Td>
                    <Table.Td>
                      <Text size="sm">
                        {new Date(member.member_created_datetime).toLocaleDateString('en-IN')}
                      </Text>
                    </Table.Td>

                    <Table.Td>
                      <Group gap="xs">
                        <ActionIcon
                          variant="light"
                          size="sm"
                          onClick={() => handleView(member)}
                        >
                          <IconEye size={16} />
                        </ActionIcon>
                        <ActionIcon
                          variant="light"
                          color="blue"
                          size="sm"
                          onClick={() => handleEdit(member)}
                        >
                          <IconEdit size={16} />
                        </ActionIcon>
                        <ActionIcon
                          variant="light"
                          color="green"
                          size="sm"
                          onClick={() => handleGenerateAutoLogin(member)}
                          title="Generate Auto-Login Link"
                        >
                          <IconLink size={16} />
                        </ActionIcon>
                        <ActionIcon
                          variant="light"
                          color="red"
                          size="sm"
                          onClick={() => handleDelete(member)}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>


                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>

          {totalPages > 1 && (
            <Group justify="center" p="md">
              <Pagination
                value={currentPage}
                onChange={setCurrentPage}
                total={totalPages}
              />
            </Group>
          )}
        </Paper>
      )}

      {/* Modals */}
      <Modal
        opened={formOpened}
        onClose={() => {
          formHandlers.close();
          setSelectedMember(null);
        }}
        title={formMode === 'create' ? 'Add New Member' : 'Edit Member'}
        size="lg"
      >
        <MemberForm
          member={selectedMember}
          batches={batches}
          mode={formMode}
          onSubmit={handleFormSubmit}
          onCancel={() => {
            formHandlers.close();
            setSelectedMember(null);
          }}
        />
      </Modal>

      <Modal
        opened={detailsOpened}
        onClose={detailsHandlers.close}
        title="Member Details"
        size="lg"
      >
        {selectedMember && (
          <MemberDetails member={selectedMember} />
        )}
      </Modal>

      <Modal
        opened={deleteOpened}
        onClose={deleteHandlers.close}
        title="Confirm Delete"
      >
        <Stack>
          <Text>
            Are you sure you want to delete member "{selectedMember?.full_name}"?
            This action cannot be undone.
          </Text>
          <Group justify="flex-end">
            <Button variant="light" onClick={deleteHandlers.close}>
              Cancel
            </Button>
            <Button color="red" onClick={confirmDelete}>
              Delete Member
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
};

export default MemberManager;